import React, { useEffect, useRef, useState } from "react";
import {
  Drawer,
  Button,
  Box,
  Typography,
  Divider,
  useTheme,
  IconButton,
  TextField,
  CircularProgress,
  Avatar,
} from "@mui/material";
import { useSnackbar } from "notistack";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import {
  ChangeStatusOfRequest,
  DietPlanFace,
  GetSignUrlForDietPlanUpload,
  UploadDietPlan,
  sendPutPdfRequest,
} from "../../services/DietplanServices";
import { MonitorWeight, Height } from "@mui/icons-material";
import AttachmentIcon from "@mui/icons-material/Attachment";
import DeleteIcon from "@mui/icons-material/Delete";
interface AddUserDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedRequest: DietPlanFace;
  onReload: () => void;
}

const DietPlanDrawer: React.FC<AddUserDrawerProps> = ({
  open,
  onClose,
  selectedRequest,
  onReload,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [comment, setComment] = useState<string>("");
  const [pdfprev, setPdfPrev] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (selectedRequest) {
      setComment(selectedRequest.comments);
      setPdfPrev(selectedRequest.dietPlanUrl);
    }
  }, [selectedRequest]);

  const handleMoveToInProgress = async () => {
    const status = await ChangeStatusOfRequest({
      id: selectedRequest.id,
      status: "IN_PROGRESS",
    });
    if (status == 204) {
      enqueueSnackbar("Moved to In Progress", { variant: "info" });
      onReload();
      onClose();
    }
  };
  const handleOnSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (!file && !comment) {
        // return enqueueSnackbar("Thumbnail is required");
        return;
      }

      setIsUploading(true);
      let thumbAwsUrl = selectedRequest.dietPlanUrl || "";
      if (file) {
        const signUrl = await GetSignUrlForDietPlanUpload({
          userId: selectedRequest.user.id,
          name: file.name,
        });
        thumbAwsUrl = signUrl;
        const upload = await sendPutPdfRequest({ signUrl, file });

        if (upload.status !== 200) {
          enqueueSnackbar("Failed to upload PDF", { variant: "error" });
          setIsUploading(false);
          return;
        }
      }

      const body = {
        requestId: selectedRequest.id,
        comment: comment,
        docUrl: thumbAwsUrl.split("?")[0],
      };
      const status = await UploadDietPlan(body);
      setIsUploading(false);
      if (status == 204) {
        enqueueSnackbar("Request Addressed Successfully!", {
          variant: "success",
        });
        onReload();
        onClose();
      } else {
        enqueueSnackbar("Failed to save video. Please try again later");
      }
      return;
    } catch (error) {
      enqueueSnackbar("Failed to save video. Please try again later");
    }
  };

  const handleUpload = (event: any) => {
    const uploadedFile = event.target.files[0];
    if (uploadedFile) {
      setFile(uploadedFile);
    } else {
      setFile(null);
    }
  };

  const handleRemove = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          backgroundColor: theme.palette.background.paper,
          borderTopLeftRadius: theme.spacing(3),
          borderBottomLeftRadius: theme.spacing(3),
          width: 450,
          height: "100vh",
          boxSizing: "border-box",
          boxShadow: theme.shadows[5],
        },
      }}
    >
      <Box
        component="form"
        aria-label="User Form"
        sx={{
          backgroundColor: theme.palette.background.paper,
          height: "100%",
          padding: theme.spacing(3),
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Box
            mb={2}
            mt={2}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{ marginRight: theme.spacing(1) }}
            >
              <KeyboardBackspaceIcon />
            </IconButton>

            <Typography variant="h5" lineHeight={0.8} fontWeight="bold">
              Diet Request <br />
              <span style={{ fontSize: 10, color: "gray" }}>
                {" "}
                {"#"}
                {selectedRequest.id}
                {/* {")"} */}
              </span>
            </Typography>
          </Box>

          <Divider sx={{ marginBottom: theme.spacing(2) }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginTop: theme.spacing(2),
              width: "100%",
            }}
          >
            <Avatar
              sx={{
                bgcolor: theme.palette.info.light,
                color: theme.palette.secondary.main,
                width: 100,
                height: 100,
                marginRight: theme.spacing(2),
                fontSize: 50,
              }}
            >
              {selectedRequest.user?.firstName.charAt(0)}
              {selectedRequest.user?.lastName.charAt(0)}
            </Avatar>
          </Box>

          <Divider sx={{ marginY: theme.spacing(2) }} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              marginBottom: theme.spacing(3),
            }}
          >
            <Box sx={{ paddingRight: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                {selectedRequest.user?.firstName}{" "}
                {selectedRequest.user?.lastName}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Email: {selectedRequest.user?.email}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Phone: {selectedRequest.user?.phone}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body1"
                style={{ color: "gray", fontWeight: "bold" }}
              >
                Age:{" "}
                {selectedRequest.user?.healthData?.age
                  ? `${selectedRequest.user?.healthData?.age} Years`
                  : "N/A"}
              </Typography>
              <Typography
                variant="body1"
                style={{ color: "gray", fontWeight: "bold" }}
              >
                Height:{" "}
                {selectedRequest.user?.healthData?.height
                  ? `${selectedRequest.user?.healthData?.height} cm`
                  : "N/A"}
              </Typography>
              <Typography
                variant="body1"
                style={{ color: "gray", fontWeight: "bold" }}
              >
                Weight:
                {selectedRequest.user?.healthData?.weight
                  ? `${selectedRequest.user?.healthData?.weight} kg`
                  : "N/A"}
              </Typography>
            </Box>
          </Box>

          {pdfprev ? (
            <Box sx={{ marginTop: theme.spacing(4) }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => window.open(pdfprev, "_blank")}
                startIcon={<AttachmentIcon />}
              >
                View PDF
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                border: `2px dashed ${
                  file ? theme.palette.success.main : theme.palette.divider
                }`,
                borderRadius: theme.spacing(1),
                padding: theme.spacing(2),
                textAlign: "center",
                marginTop: theme.spacing(4),
                position: "relative",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                  cursor: "pointer",
                },
              }}
              onClick={() =>
                fileInputRef.current && fileInputRef.current.click()
              }
            >
              <input
                type="file"
                ref={fileInputRef}
                accept=".pdf"
                style={{ display: "none" }}
                onChange={handleUpload}
              />
              <label
                htmlFor="upload-file"
                style={{ display: "block", cursor: "pointer" }}
              >
                {!file && (
                  <>
                    <UploadFileIcon color="action" />
                    <Typography variant="body1" color="textSecondary">
                      Upload PDF
                    </Typography>
                  </>
                )}
              </label>
              {file && (
                <>
                  <UploadFileIcon color="action" />
                  <Typography variant="body1" color="textSecondary">
                    {file.name}
                  </Typography>
                  <IconButton
                    sx={{
                      position: "absolute",
                      top: theme.spacing(1),
                      right: theme.spacing(1),
                      backgroundColor: theme.palette.background.paper,
                    }}
                    onClick={handleRemove}
                  >
                    <DeleteIcon color="error" />
                  </IconButton>
                </>
              )}
            </Box>
          )}

          <TextField
            label="Comments"
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={!!selectedRequest.comments}
            sx={{ marginTop: theme.spacing(4) }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: theme.spacing(4),
          }}
        >
          {selectedRequest.requestStatus === "REQUESTED" && (
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              onClick={handleMoveToInProgress}
              sx={{
                fontSize: "1rem",
                borderRadius: theme.spacing(1),
              }}
            >
              Move To In Progress
            </Button>
          )}
          {selectedRequest.requestStatus === "IN_PROGRESS" && (
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              disabled
              sx={{
                fontSize: "1rem",
                borderRadius: theme.spacing(1),
              }}
            >
              Move to Addressed
            </Button>
          )}
          {/* {selectedRequest.requestStatus === "ADDRESSED" && (
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              disabled
              sx={{
                fontSize: "1rem",
                borderRadius: theme.spacing(1),
              }}
            >
              Deactivate Diet Plan
            </Button>
          )} */}
          <Button
            disabled={
              isUploading ||
              (!comment && !file) ||
              selectedRequest.requestStatus === "ADDRESSED"
            }
            type="submit"
            variant="contained"
            color="primary"
            onClick={handleOnSubmit}
            sx={{
              fontSize: "1rem",
              borderRadius: theme.spacing(1),
            }}
          >
            {isUploading ? <CircularProgress size={24} /> : "Addressed"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default DietPlanDrawer;
